import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '../features/auth/contexts/useAuth';
import { SkeletonStyles } from '../shared/components/ui/SkeletonLoader';
import { PublicLayout } from '../shared/components/layout/PublicLayout';
import { AuthenticatedLayout } from '../shared/components/layout/AuthenticatedLayout';
import { ProtectedRoute } from '../features/auth/components/ProtectedRoute';
import { PublicOnlyRoute } from '../features/auth/components/PublicOnlyRoute';
import { LoadingState } from '../shared/components/ui/LoadingState';
import { ErrorBoundary } from '../shared/components/ErrorBoundary';

// ─── Public pages ────────────────────────────────────────────────────────────
import { Home } from '../shared/pages/public/Home';
import { About } from '../shared/pages/public/About';
import { Contact } from '../shared/pages/public/Contact';
import { Blog } from '../shared/pages/public/Blog';
import { FAQ } from '../shared/pages/public/FAQ';
import { Terms } from '../shared/pages/public/Terms';
import { Privacy } from '../shared/pages/public/Privacy';
import { Cancellation } from '../shared/pages/public/Cancellation';
import { UnauthorizedPage } from '../shared/pages/public/UnauthorizedPage';

// ─── Auth pages ──────────────────────────────────────────────────────────────
import { LoginForm } from '../features/auth/components/LoginForm';
import { RegisterForm } from '../features/auth/forms/RegisterForm';
const ForgotPassword = React.lazy(() =>
  import('../features/auth/pages/ForgotPassword').then(module => ({
    default: module.default,
  }))
);

// ─── Feature pages (lazy loaded) ────────────────────────────────────────────
const Dashboard = React.lazy(() =>
  import('../features/dashboard/pages/Dashboard').then(module => ({
    default: module.Dashboard,
  }))
);
const Profile = React.lazy(() =>
  import('../features/profile/pages/Profile').then(module => ({
    default: module.Profile,
  }))
);
const Company = React.lazy(() =>
  import('../features/profile/pages/Company').then(module => ({
    default: module.Company,
  }))
);
const MyBookings = React.lazy(() =>
  import('../features/bookings/pages/MyBookings').then(module => ({
    default: module.MyBookings,
  }))
);
const BookingDetail = React.lazy(() =>
  import('../features/bookings/pages/BookingDetail').then(module => ({
    default: module.BookingDetail,
  }))
);
const SafariList = React.lazy(() =>
  import('../features/safaris/pages/SafariSearch').then(module => ({
    default: module.SafariList,
  }))
);
const SafariDetails = React.lazy(() =>
  import('../features/safaris/pages/SafariDetails').then(module => ({
    default: module.SafariDetails,
  }))
);
const TripList = React.lazy(() =>
  import('../features/trips/pages/TripList').then(module => ({
    default: module.TripList,
  }))
);
const TripDiscovery = React.lazy(() =>
  import('../features/trips/pages/TripDiscovery').then(module => ({
    default: module.TripDiscovery,
  }))
);
const TripDetails = React.lazy(() =>
  import('../features/trips/pages/TripDetails').then(module => ({
    default: module.TripDetails,
  }))
);
const BookingConfirmation = React.lazy(() =>
  import('../features/bookings/pages/BookingConfirmation').then(module => ({
    default: module.BookingConfirmation,
  }))
);
const BookingSuccess = React.lazy(() =>
  import('../features/bookings/pages/BookingSuccess').then(module => ({
    default: module.BookingSuccess,
  }))
);
const VehicleManagement = React.lazy(() =>
  import('../features/misc/pages/VehicleManagement').then(module => ({
    default: module.VehicleManagement,
  }))
);
const VerificationRequired = React.lazy(() =>
  import('../features/auth/components/VerificationRequired').then(module => ({
    default: module.VerificationRequired,
  }))
);
const SavedSafaris = React.lazy(() =>
  import('../features/safaris/pages/SavedSafaris').then(module => ({
    default: module.SavedSafaris,
  }))
);
const Reviews = React.lazy(() =>
  import('../features/reviews/pages/Reviews').then(module => ({
    default: module.Reviews,
  }))
);
const Deals = React.lazy(() =>
  import('../features/misc/pages/Deals').then(module => ({
    default: module.Deals,
  }))
);
const Payments = React.lazy(() =>
  import('../features/payments/pages/Payments').then(module => ({
    default: module.Payments,
  }))
);
const TripManagement = React.lazy(() =>
  import('../features/trips/pages/TripManagement').then(module => ({
    default: module.TripManagement,
  }))
);
const MyTrips = React.lazy(() =>
  import('../features/trips/pages/MyTrips').then(module => ({
    default: module.MyTrips,
  }))
);
const Fleet = React.lazy(() =>
  import('../features/fleet/pages/Fleet').then(module => ({
    default: module.Fleet,
  }))
);
const Safaris = React.lazy(() =>
  import('../features/safaris/pages/Safaris').then(module => ({
    default: module.Safaris,
  }))
);
const Passengers = React.lazy(() =>
  import('../features/dashboard/pages/Passengers').then(module => ({
    default: module.Passengers,
  }))
);
const Messages = React.lazy(() =>
  import('../features/dashboard/pages/Messages').then(module => ({
    default: module.Messages,
  }))
);
const Earnings = React.lazy(() =>
  import('../features/dashboard/pages/Earnings').then(module => ({
    default: module.Earnings,
  }))
);
const Schedule = React.lazy(() =>
  import('../features/dashboard/pages/Schedule').then(module => ({
    default: module.Schedule,
  }))
);
const Marketplace = React.lazy(() =>
  import('../features/marketplace/pages/Marketplace').then(module => ({
    default: module.Marketplace,
  }))
);
const HireRequests = React.lazy(() =>
  import('../features/hire-requests/pages/HireRequests').then(module => ({
    default: module.HireRequests,
  }))
);
const SettingsPage = React.lazy(() =>
  import('../features/settings/pages/Settings').then(module => ({
    default: module.SettingsPage,
  }))
);

/**
 * App — Dual layout routing (like Laravel's layouts.public / layouts.app).
 *
 * PublicLayout  → floating navbar + footer  (marketing, discovery, auth pages)
 * AuthenticatedLayout → sidebar + top bar   (dashboard, bookings, fleet, etc.)
 *
 * Each route group is nested under its layout using <Outlet />.
 * Individual pages no longer need to import or wrap in DashboardLayout.
 */
function App() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingState message="Loading application..." />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingState message="Loading page..." />}>
        <Routes>
          {/* ══════════════════════════════════════════════════════════════
              PUBLIC LAYOUT — floating navbar + footer
              Like Laravel: @extends('layouts.public')
              ═════════════════════════════════════════════════════════════ */}
          <Route element={<PublicLayout />}>
            {/* Static / marketing pages */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cancellation" element={<Cancellation />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />

            {/* Auth pages — only accessible when logged out */}
            <Route path="/login" element={<PublicOnlyRoute><LoginForm /></PublicOnlyRoute>} />
            <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
            <Route path="/register" element={<PublicOnlyRoute><RegisterForm /></PublicOnlyRoute>} />

            {/* Public discovery / browsing pages */}
            <Route path="/safaris" element={<SafariList />} />
            <Route path="/safaris-discovery" element={<SafariList />} />
            <Route path="/safaris/:id" element={<SafariDetails />} />
            <Route path="/transport-trips" element={<TripList />} />
            <Route path="/trips-discovery" element={<TripDiscovery />} />
            <Route path="/trips/:id" element={<TripDetails />} />
          </Route>

          {/* ══════════════════════════════════════════════════════════════
              AUTHENTICATED LAYOUT — sidebar + top bar (no floating nav)
              Like Laravel: @extends('layouts.app')
              ═════════════════════════════════════════════════════════════ */}
          <Route element={<ProtectedRoute><AuthenticatedLayout /></ProtectedRoute>}>
            {/* Dashboard */}
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/dashboard/trips" element={<ProtectedRoute requiredRole="operator"><MyTrips /></ProtectedRoute>} />
            <Route path="/dashboard/trips/:id" element={<ProtectedRoute requiredRole="operator"><TripDetails /></ProtectedRoute>} />
            <Route path="/dashboard/schedule" element={<ProtectedRoute requiredRole="operator"><Schedule /></ProtectedRoute>} />
            <Route path="/dashboard/passengers" element={<ProtectedRoute requiredRole="operator"><Passengers /></ProtectedRoute>} />
            <Route path="/dashboard/messages" element={<Messages />} />
            <Route path="/dashboard/messages/:conversationId" element={<Messages />} />
            <Route path="/dashboard/earnings" element={<ProtectedRoute requiredRole="operator"><Earnings /></ProtectedRoute>} />

            {/* Bookings */}
            <Route path="/bookings" element={<MyBookings />} />
            <Route path="/bookings/:id" element={<BookingDetail />} />
            <Route path="/booking/confirm" element={<BookingConfirmation />} />
            <Route path="/booking/success" element={<BookingSuccess />} />

            {/* Profile & Company */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/company" element={<ProtectedRoute requiredRole="operator"><Company /></ProtectedRoute>} />

            {/* Trips & Safari Management */}
            <Route path="/trips" element={<TripManagement />} />
            <Route path="/safaris-management" element={<ProtectedRoute requiredRole="operator"><Safaris /></ProtectedRoute>} />

            {/* Fleet */}
            <Route path="/fleet" element={<ProtectedRoute requiredRole="operator"><Fleet /></ProtectedRoute>} />
            <Route path="/vehicles" element={<ProtectedRoute requiredRole="operator"><VehicleManagement /></ProtectedRoute>} />

            {/* Hire Requests & Settings */}
            <Route path="/hire-requests" element={<ProtectedRoute requiredRole="operator"><HireRequests /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute requiredRole="operator"><SettingsPage /></ProtectedRoute>} />

            {/* Other authenticated pages */}
            <Route path="/saved" element={<SavedSafaris />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/verification-required" element={<VerificationRequired />} />
            <Route path="/marketplace" element={<Marketplace />} />
            <Route path="/marketplace/vehicles" element={<Marketplace />} />
            <Route path="/marketplace/partners" element={<Marketplace />} />
          </Route>
        </Routes>
      </Suspense>

      <Toaster position="top-right" />
      <SkeletonStyles />
    </ErrorBoundary>
  );
}

export default App;
